// Largely derived from the following:
// https://github.com/iamhosseindhv/notistack/blob/master/examples/redux-example/Notifier.js

import { differenceBy } from 'es-toolkit';
import React, { Component } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import {
  closeSnackbar,
  enqueueSnackbar,
  OptionsWithExtraProps as SnackbarOptions,
  SnackbarKey,
  VariantType as SnackbarType,
} from 'notistack';

import {
  closeSnackbar as removeSnackbarFromReduxState,
  Notification,
  StateWithNotifications,
} from '../store/notificationsSlice';
import { NotificationActionButton } from './NotificationActionButton';
import { SnackbarDismissButton } from './SnackbarDismissButton';

export type Snackbar = [
  string,
  Omit<SnackbarOptions<SnackbarType>, 'onExited'> &
    Required<Pick<SnackbarOptions<SnackbarType>, 'key'>> & {
      /** utc timestamp of when the notification was first created */
      addedAt: number;

      /**
       * If true, signifies that this notification is related to an ongoing user interaction. The design client will
       *   ensure that such an interaction is always visible.
       */
      interactive?: boolean;

      /**
       * Mirrors `Notification.permanent`: the notification stays until whatever raised it takes it down again. The
       *   design client gives these precedence over every other notification, including `interactive` ones, and offers
       *   the user no way to dismiss them.
       */
      permanent?: boolean;
    },
];

export const dismissAction = (key: SnackbarKey) => (
  <SnackbarDismissButton
    notificationKey={key}
    size="medium"
    sx={{
      borderColor: '#ffffff',
      color: '#ffffff',
      '&:hover': {
        backgroundColor: '#ffffff',
        borderColor: '#ffffff',
        color: '#2d2d2d',
      },
    }}
  />
);

interface OwnProps {
  /**
   * If set, these notifications will be displayed in addition to those in the redux state.
   */
  readonly notifications?: Notification[];

  /**
   * If set, these snackbars will be displayed (exactly as-is, with no modifications, so make sure your keys are unique,
   *   etc.) in addition to those in the redux state.
   */
  readonly snackbars?: Snackbar[];
}

type Props = OwnProps & StateProps & DispatchProps;

export function createSnackbar({
  key,
  addedAt,
  message,
  options,
  permanent,
  waitForDismissal,
  linkButton,
}: Notification): Snackbar {
  const showDismiss = waitForDismissal && !permanent;

  const action =
    showDismiss || linkButton
      ? (snackbarKey: SnackbarKey) => (
          <>
            {linkButton && (
              <NotificationActionButton
                href={linkButton.href}
                /** @ts-expect-error "Property 'target' does not exist on type" */
                target="_blank"
                onClick={(event) => event.stopPropagation()}
              >
                {linkButton.text}
              </NotificationActionButton>
            )}
            {showDismiss && dismissAction(snackbarKey)}
          </>
        )
      : null;

  return [message, { key, addedAt, ...options, permanent, persist: waitForDismissal || permanent, action }];
}

class NotifierComponent extends Component<Props> {
  private displaySnackbar = ([message, options]: Snackbar) => {
    enqueueSnackbar(message, {
      ...options,
      onExited: () => {
        if (!options.persist) {
          this.props.removeSnackbarFromReduxState(options.key);
        }
      },
    });
  };

  componentDidMount() {
    this.props.snackbars.forEach(this.displaySnackbar);
  }

  componentDidUpdate(prevProps: Props) {
    const createdSnackbars = differenceBy(this.props.snackbars, prevProps.snackbars, ([_, options]) => options.key);

    if (createdSnackbars.length > 0) {
      createdSnackbars.forEach(this.displaySnackbar);
    }

    const removedSnackbars = differenceBy(prevProps.snackbars, this.props.snackbars, ([_, options]) => options.key);
    for (const removedSnackbar of removedSnackbars) {
      closeSnackbar(removedSnackbar[1].key);
    }
  }

  render() {
    return null;
  }
}

type StateProps = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: StateWithNotifications, ownProps: OwnProps) => {
  const selectorSnackbars = ownProps.snackbars ?? [];

  const stateNotifications = state.notifications.snackbars;
  const selectorNotifications = ownProps.notifications ?? [];

  return {
    snackbars: [
      ...selectorSnackbars,
      ...stateNotifications.map(createSnackbar),
      ...selectorNotifications.map(createSnackbar),
    ],
  };
};

type DispatchProps = ResolveThunks<typeof actionCreators>;
const actionCreators = {
  removeSnackbarFromReduxState,
};

export const Notifier = connect(mapStateToProps, actionCreators)(NotifierComponent);
